'use client'

import {
  BROWSER_METRIC_ENDPOINT,
  BROWSER_METRIC_EXIT_BODY_BYTES,
  BROWSER_METRIC_MAX_BATCH,
  BROWSER_METRIC_MAX_BODY_BYTES,
  type BrowserMetricPoint,
  BROWSER_METRIC_MAX_QUEUE as MAX_QUEUE,
} from './browser-metric-contract'
import type { MetricObservation } from './metrics'
import { incrementTelemetryHealth } from './telemetry-health'

export * from './browser-metric-contract'

const TIMEOUT_MS = 2000
const FLUSH_DELAY_MS = 2000

export type BrowserMetricTransport = {
  enqueue: (observation: MetricObservation) => boolean
  flush: (keepalive?: boolean) => Promise<void>
  dispose: () => void
  snapshot: () => Readonly<{
    queued: number
    inFlight: number
    relayAcceptedBatches: number
    failedPoints: number
    droppedPoints: number
  }>
}

/** Bounded best-effort delivery. A relay 202 is not proof of Datadog indexing. */
export function createBrowserMetricTransport(
  options: { fetch?: typeof globalThis.fetch } = {}
): BrowserMetricTransport {
  const fetcher = options.fetch ?? globalThis.fetch.bind(globalThis)
  let queue: Array<{ point: BrowserMetricPoint; json: string; bytes: number }> =
    []
  let pending: ReturnType<typeof setTimeout> | undefined
  let active: Promise<void> | undefined
  let exitActive: Promise<void> | undefined
  const controllers = new Set<AbortController>()
  const responseReaders = new Set<ReadableStreamDefaultReader<Uint8Array>>()
  let disposed = false
  let inFlight = 0
  let relayAcceptedBatches = 0
  let failedPoints = 0
  let droppedPoints = 0

  function drop(count: number): void {
    droppedPoints += count
    incrementTelemetryHealth('collector_buffer_overflow', count)
  }

  function schedule(): void {
    if (!disposed && queue.length && !pending && !active && !exitActive) {
      pending = setTimeout(() => {
        pending = undefined
        void flush()
      }, FLUSH_DELAY_MS)
    }
  }

  function enqueue(observation: MetricObservation): boolean {
    if (disposed || queue.length >= MAX_QUEUE) {
      drop(1)
      return false
    }
    try {
      // Snapshot primitive metric data only. Correlation IDs stay in RUM/spans.
      const point: BrowserMetricPoint = Object.freeze({
        name: observation.metric.name,
        type: observation.metric.type,
        unit: observation.metric.unit,
        value: observation.metric.value,
        tags: Object.freeze({ ...observation.metric.tags }),
      })
      const json = JSON.stringify(point)
      const bytes = new TextEncoder().encode(json).byteLength
      if (bytes > 4096) {
        drop(1)
        return false
      }
      queue.push({ point, json, bytes })
      schedule()
      return true
    } catch {
      drop(1)
      return false
    }
  }

  function flush(keepalive = false): Promise<void> {
    if (
      disposed ||
      (keepalive ? exitActive : active || exitActive) ||
      queue.length === 0
    ) {
      return (
        (keepalive ? exitActive : active || exitActive) ?? Promise.resolve()
      )
    }
    if (pending) {
      clearTimeout(pending)
      pending = undefined
    }
    const batch: Array<BrowserMetricPoint> = []
    const serialized: Array<string> = []
    const prefix = '{"schema_version":1,"metrics":['
    const suffix = ']}'
    let bytes = prefix.length + suffix.length
    const maxBatch = keepalive ? MAX_QUEUE : BROWSER_METRIC_MAX_BATCH
    const maxBytes = keepalive
      ? BROWSER_METRIC_EXIT_BODY_BYTES
      : BROWSER_METRIC_MAX_BODY_BYTES
    while (queue.length && batch.length < maxBatch) {
      const item = queue[0]
      const nextBytes = bytes + item.bytes + (batch.length ? 1 : 0)
      if (nextBytes > maxBytes) {
        break
      }
      queue.shift()
      batch.push(item.point)
      serialized.push(item.json)
      bytes = nextBytes
    }
    if (batch.length === 0) {
      queue.shift()
      drop(1)
      schedule()
      return Promise.resolve()
    }
    if (keepalive && queue.length) {
      // Browsers cannot guarantee delivery beyond the finite unload budget.
      drop(queue.length)
      queue = []
    }
    inFlight += batch.length
    const body = prefix + serialized.join(',') + suffix
    const controller = new AbortController()
    controllers.add(controller)
    const signal = controller.signal
    const run = async () => {
      let timer: ReturnType<typeof setTimeout> | undefined
      let responseReader: ReadableStreamDefaultReader<Uint8Array> | undefined
      try {
        const timeout = new Promise<never>((_, reject) => {
          timer = setTimeout(() => {
            controller.abort()
            void responseReader?.cancel().catch(() => {})
            reject(new Error('metric relay timeout'))
          }, TIMEOUT_MS)
        })
        const submit = async () => {
          const response = await fetcher(BROWSER_METRIC_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            credentials: 'same-origin',
            mode: 'same-origin',
            cache: 'no-store',
            redirect: 'error',
            keepalive,
            signal,
          })
          if (signal.aborted) {
            void response.body?.cancel().catch(() => {})
            return 0
          }
          if (response.status === 202) {
            void response.body?.cancel().catch(() => {})
            return batch.length
          }
          if (response.status !== 207 || !response.body) {
            void response.body?.cancel().catch(() => {})
            return 0
          }
          const reader = response.body.getReader()
          responseReader = reader
          responseReaders.add(reader)
          let text = ''
          let bytes = 0
          const decoder = new TextDecoder('utf-8', { fatal: true })
          try {
            while (true) {
              // biome-ignore lint/performance/noAwaitInLoops: Read acknowledgement chunks serially under the response deadline.
              const chunk = await reader.read()
              if (chunk.done) {
                break
              }
              bytes += chunk.value.byteLength
              if (bytes > 128) {
                return 0
              }
              text += decoder.decode(chunk.value, { stream: true })
            }
            text += decoder.decode()
            const result: unknown = JSON.parse(text)
            const accepted =
              result && typeof result === 'object' && 'accepted' in result
                ? result.accepted
                : undefined
            return typeof accepted === 'number' &&
              Number.isSafeInteger(accepted) &&
              accepted >= 0 &&
              accepted <= batch.length
              ? accepted
              : 0
          } finally {
            responseReaders.delete(reader)
            void reader.cancel().catch(() => {})
          }
        }
        const accepted = await Promise.race([submit(), timeout])
        if (accepted === batch.length) {
          relayAcceptedBatches += 1
        } else {
          failedPoints += batch.length - accepted
          incrementTelemetryHealth(
            'metric_sink_failed',
            batch.length - accepted
          )
        }
      } catch {
        failedPoints += batch.length
        incrementTelemetryHealth('metric_sink_failed', batch.length)
      } finally {
        if (timer) {
          clearTimeout(timer)
        }
        inFlight -= batch.length
        controllers.delete(controller)
      }
    }
    const promise = run().finally(() => {
      if (keepalive) {
        exitActive = undefined
      } else {
        active = undefined
      }
      schedule()
    })
    if (keepalive) {
      exitActive = promise
    } else {
      active = promise
    }
    // Never retry an ambiguous submission: increments might already be accepted.
    return promise
  }

  function dispose(): void {
    disposed = true
    if (pending) {
      clearTimeout(pending)
    }
    pending = undefined
    drop(queue.length)
    queue = []
    for (const controller of controllers) {
      controller.abort()
    }
    for (const reader of responseReaders) {
      void reader.cancel().catch(() => {})
    }
  }

  return {
    enqueue,
    flush,
    dispose,
    snapshot: () =>
      Object.freeze({
        queued: queue.length,
        inFlight,
        relayAcceptedBatches,
        failedPoints,
        droppedPoints,
      }),
  }
}
